import React, { useRef, useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  verifyResetCode,
  resetPassword,
  postForgotPassword,
} from "../../services/api";
import Alert from "../../components/common/Alert";
import { Notify } from "notiflix/build/notiflix-notify-aio";
import { useTranslation } from "react-i18next";

export default function ForgotPasswordConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const { t } = useTranslation();

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(300);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("error");
  const inputRefs = [
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
  ];

  useEffect(() => {
    if (timer === 0) return;
    const interval = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleCodeChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (!val) return;
    const newCode = [...code];
    newCode[idx] = val[val.length - 1];
    setCode(newCode);
    if (idx < 5 && val) inputRefs[idx + 1].current.focus();
  };

  const handleCodeKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (code[idx]) {
        const newCode = [...code];
        newCode[idx] = "";
        setCode(newCode);
      } else if (idx > 0) {
        inputRefs[idx - 1].current.focus();
      }
    }
  };

  // Tüm inputların dolu olup olmadığını kontrol et
  const areAllInputsFilled = () => {
    const codeStr = code.join("");
    return (
      codeStr.length === 6 && password.length >= 6 && password2.length >= 6
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const codeStr = code.join("");
    if (codeStr.length !== 6) {
      setError(t("enterVerificationCode"));
      return;
    }

    if (password.length < 6) {
      setError(t("passwordMinLength"));
      return;
    }

    if (password !== password2) {
      // Notiflix error notification
      Notify.failure(t("passwordMismatch"));
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(email, codeStr, password);
      Notify.success(t("passwordResetSuccess"));
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      // Notiflix error notification for API errors
      const errorMessage =
        err?.response?.data?.message || t("passwordResetError");
      Notify.failure(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen flex bg-white">
      {/* Sol taraf - Form Container */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white p-6">
          <Link to="/login" className="flex items-center justify-center ">
            <img
              src="/assets/qriendLogoRedNew.png"
              alt="QRiend Logo"
              className="w-56 h-28"
            />
          </Link>

          <h2 className="text-2xl font-semibold font-poppins text-center text-[#222] mb-2">
            {t("passwordReset")}
          </h2>
          <p className="text-center text-gray-500 text-sm mb-6">
            {t("passwordResetDesc")}
          </p>

          <form onSubmit={handleSubmit}>
            {/* Doğrulama Kodu */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("verificationCode")}
              </label>
              <div className="flex justify-center gap-2 mb-2">
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={inputRefs[idx]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className={`w-10 h-10 text-xl text-center bg-white border-2 rounded-lg focus:outline-none text-pink-600 focus:ring-2 focus:ring-pink-400 ${
                      digit ? "border-pink-400" : "border-gray-300"
                    }`}
                    value={digit}
                    onChange={(e) => handleCodeChange(e, idx)}
                    onKeyDown={(e) => handleCodeKeyDown(e, idx)}
                    autoFocus={idx === 0}
                  />
                ))}
              </div>
              <div className="text-center text-gray-500 text-xs">
                {t("codeValidityTime")}{" "}
                <span className="text-[#E94057] font-semibold">
                  {Math.floor(timer / 60)}:
                  {(timer % 60).toString().padStart(2, "0")}
                </span>
              </div>
            </div>

            {/* Yeni Şifre */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("newPassword")}
              </label>
              <div className="relative">
                <input
                  type={show1 ? "text" : "password"}
                  className="bg-white text-black w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400"
                  placeholder={t("newPasswordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShow1((s) => !s)}
                  tabIndex={-1}
                >
                  {show1 ? (
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
                      <circle cx="10" cy="10" r="3" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path d="M1 1l18 18M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
                      <circle cx="10" cy="10" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Şifre Onayı */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("confirmPassword")}
              </label>
              <div className="relative">
                <input
                  type={show2 ? "text" : "password"}
                  className="bg-white text-black w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-400"
                  placeholder={t("confirmPasswordPlaceholder")}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShow2((s) => !s)}
                  tabIndex={-1}
                >
                  {show2 ? (
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
                      <circle cx="10" cy="10" r="3" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path d="M1 1l18 18M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
                      <circle cx="10" cy="10" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !areAllInputsFilled() || timer === 0}
              className="w-full py-2 rounded-full text-white font-semibold text-lg bg-gradient-to-r from-[#FA3091] to-[#E94057] shadow-md hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t("resettingPassword") : t("resetPasswordButton")}
            </button>

            {error && (
              <div className="text-red-500 text-sm text-center mt-4">
                {error}
              </div>
            )}

            <h2 className="text-gray-400 text-sm md:text-md flex justify-center pt-4">
              {t("checkSpamFolder")}
            </h2>
          </form>
        </div>
      </div>

      {/* Sağ taraf - Görsel */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-8">
        <img
          src="/assets/forgotPassImg.png"
          alt={t("passwordReset")}
          className="w-full h-full object-contain"
        />
      </div>

      <Alert
        message={alertMessage}
        type={alertType}
        isVisible={showAlert}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
}
